"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

const slides = [
{
  id: 1,
  image: "https://images.unsplash.com/photo-1623745493581-2f7b3d0d2140",
  alt: "Wildebeest migration across golden savanna at Maasai Mara National Reserve Kenya with dramatic sunset sky",
  title: "Maasai Mara",
  subtitle: "National Reserve",
  county: "Narok County",
  tagline: "The World's Greatest Wildlife Spectacle"
},
{
  id: 2,
  image: "https://images.unsplash.com/photo-1722697798805-0500b5972549",
  alt: "Pristine white sand beach with turquoise Indian Ocean water at Diani Beach Kenya with palm trees",
  title: "Diani Beach",
  subtitle: "Indian Ocean Coast",
  county: "Kwale County",
  tagline: "Kenya\'s Crown Jewel on the Indian Ocean"
},
{
  id: 3,
  image: "https://images.unsplash.com/photo-1525537215239-0eb96d43c7bd",
  alt: "Snow-capped Mount Kenya peak rising above equatorial forest with dramatic clouds at sunrise",
  title: "Mount Kenya",
  subtitle: "National Park & Forest",
  county: "Nyeri County",
  tagline: "Africa\'s Second Highest Peak"
},
{
  id: 4,
  image: "https://images.unsplash.com/photo-1716754430696-22912c597421",
  alt: "Ancient coral stone buildings and narrow winding streets of Lamu Old Town UNESCO World Heritage Site Kenya",
  title: "Lamu Old Town",
  subtitle: "UNESCO World Heritage",
  county: "Lamu County",
  tagline: "East Africa\'s Oldest Living Town"
}];


export default function HeroCarousel() {
  const [current, setCurrent] = useState(0);
  const [transitioning, setTransitioning] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setTransitioning(true);
      setTimeout(() => {
        setCurrent((prev) => (prev + 1) % slides.length);
        setTransitioning(false);
      }, 600);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const goTo = (idx: number) => {
    if (idx === current) return;
    setTransitioning(true);
    setTimeout(() => {
      setCurrent(idx);
      setTransitioning(false);
    }, 400);
  };

  return (
    <section className="relative min-h-screen flex items-end overflow-hidden bg-kenya-black">
      {/* Slides */}
      {slides.map((slide, idx) =>
      <div
        key={slide.id}
        className={`absolute inset-0 transition-opacity duration-1000 ${
        idx === current ? "opacity-100" : "opacity-0"}`
        }
        aria-hidden={idx !== current}>
        
          <AppImage
          src={slide.image}
          alt={slide.alt}
          fill
          priority={idx === 0}
          className="object-cover"
          sizes="100vw" />
        
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-kenya-black/20 via-kenya-black/45 to-kenya-black/92" />
          {/* Left vignette */}
          <div className="absolute inset-0 bg-gradient-to-r from-kenya-black/65 via-transparent to-transparent" />
        </div>
      )}

      {/* Maasai shield watermark */}
      <div className="absolute top-1/2 right-12 -translate-y-1/2 w-96 h-96 opacity-[0.04] pointer-events-none hidden xl:block">
        <svg viewBox="0 0 200 260" fill="white" xmlns="http://www.w3.org/2000/svg">
          <path d="M100 5 L195 50 L195 130 Q195 200 100 255 Q5 200 5 130 L5 50 Z" />
          <path d="M100 5 L195 50 L195 130 Q195 200 100 255 Q5 200 5 130 L5 50 Z" fill="none" stroke="white" strokeWidth="3" />
          <line x1="100" y1="5" x2="100" y2="255" stroke="white" strokeWidth="4" />
          <line x1="5" y1="90" x2="195" y2="90" stroke="white" strokeWidth="2" />
          <line x1="5" y1="170" x2="195" y2="170" stroke="white" strokeWidth="2" />
        </svg>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-8xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24 pt-24 sm:pt-32">
        <div
          className={`transition-all duration-700 ${
          transitioning ? "opacity-0 translate-y-4" : "opacity-100 translate-y-0"}`
          }>
          
          {/* County pill */}
          <div className="inline-flex items-center gap-2 mb-4 sm:mb-6 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-white/25 bg-white/8 backdrop-blur-sm">
            <div className="w-2 h-2 rounded-full bg-kenya-green pulse-dot" />
            <span className="text-[0.625rem] sm:text-[0.6875rem] font-bold uppercase tracking-[0.4em] text-white/80 font-condensed">
              {slides[current].county}
            </span>
          </div>

          {/* Main headline */}
          <h1
            className="font-display font-bold text-white mb-3 sm:mb-4"
            style={{ fontSize: "clamp(2.5rem, 10vw, 8.5rem)", lineHeight: "0.88", letterSpacing: "-0.03em" }}
          >
            {slides[current].title}
            <br />
            <span className="font-display italic font-light text-white/45 text-[0.6em]">
              {slides[current].subtitle}
            </span>
          </h1>

          <p className="text-white/75 text-base sm:text-xl mb-7 sm:mb-10 max-w-lg font-medium leading-relaxed">
            {slides[current].tagline}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
            <Link
              href="/product-submission"
              className="group flex items-center justify-center gap-3 bg-kenya-red text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-xl font-bold text-sm sm:text-base tracking-[0.05em] hover:bg-kenya-red/90 transition-all shadow-kenya">
              Submit Your Tourism Product
              <Icon name="ArrowRightIcon" size={17} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              href="/sign-up"
              className="flex items-center justify-center gap-3 border-2 border-white/35 text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-xl font-bold text-sm sm:text-base tracking-[0.05em] hover:bg-white/12 transition-all backdrop-blur-sm">
              Create Account
            </Link>
          </div>

          {/* Stats row */}
          <div className="mt-10 sm:mt-16 flex flex-wrap gap-6 sm:gap-10 pt-6 sm:pt-8 border-t border-white/15">
            {[
            { value: "47", label: "Counties Covered" },
            { value: "1,240+", label: "Products Submitted" },
            { value: "38", label: "Embassies Reached" },
            { value: "94%", label: "Approval Rate" }].
            map((stat) =>
            <div key={stat.label} className="space-y-1 sm:space-y-1.5">
                <div className="text-2xl sm:text-3xl font-display font-bold text-white">{stat.value}</div>
                <div className="text-[0.625rem] sm:text-[0.6875rem] font-bold uppercase tracking-[0.3em] text-white/50 font-condensed">{stat.label}</div>
              </div>
            )}
          </div>
        </div>

        {/* Slide indicators */}
        <div className="absolute bottom-6 sm:bottom-8 right-4 sm:right-6 flex flex-col gap-2">
          {slides.map((_, idx) =>
          <button
            key={idx}
            onClick={() => goTo(idx)}
            aria-label={`Go to slide ${idx + 1}`}
            className={`transition-all duration-300 rounded-full ${
            idx === current ? "w-2 h-9 bg-kenya-red" : "w-2 h-3 bg-white/25 hover:bg-white/50"}`
            } />
          )}
        </div>
      </div>
    </section>);

}